import { useCallback, useEffect, useState } from "react";
import { API_ENDPOINT } from "src/constants/endpoints";
import { useAuth } from "src/contexts/auth-provider";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { failureToast } from "src/utils/toasts";

interface PaymentAccountStatus {
  provider?: string;
  status?: string;
  onboardingStatus?: string;
  merchantStatus?: string;
  canAcceptPayments?: boolean;
  action?: "START" | "REFRESH" | "WAIT" | "NONE";
  onboardingUrl?: string;
  message?: string;
}

interface FinixOnboardingForm {
  id: number;
  finixOnboardingFormId: string;
  finixIdentityId?: string | null;
  status?: string | null;
  current?: boolean;
  action?: "START" | "REFRESH" | "WAIT" | "NONE";
  onboardingUrl?: string | null;
  onboardingUrlExpiresAt?: string | number | null;
  submittedAt?: string | number | null;
  lastSyncedAt?: string | number | null;
  createdAt?: string | number | null;
  updatedAt?: string | number | null;
}

const cardClass =
  "bg-[#ffffff] rounded-[16px] px-6 py-5 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_rgba(0,0,0,0.04)]";

const statusLabels: Record<string, string> = {
  NOT_STARTED: "Not started",
  IN_PROGRESS: "Setup in progress",
  COMPLETED: "Completed",
  PROVISIONING: "In review",
  APPROVED: "Approved",
  UPDATE_REQUESTED: "Update requested",
  REJECTED: "Rejected",
  ERROR: "Error",
};

const PaymentAccountSetting = () => {
  const { axBe } = useAxiosInterceptor();
  const { hasRestaurant } = useAuth();
  const [status, setStatus] = useState<PaymentAccountStatus | null>(null);
  const [forms, setForms] = useState<FinixOnboardingForm[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [openingFormId, setOpeningFormId] = useState<string | null>(null);

  const storeCorrelationId = hasRestaurant?.correlationId;

  const loadStatus = useCallback(async () => {
    if (!storeCorrelationId) return;
    setLoading(true);
    try {
      const [statusResponse, formsResponse] = await Promise.all([
        axBe.get(`${API_ENDPOINT}finix/merchant-onboarding/status/${storeCorrelationId}`),
        axBe.get(`${API_ENDPOINT}finix/merchant-onboarding/forms/${storeCorrelationId}`),
      ]);
      setStatus(statusResponse.data);
      setForms(formsResponse.data || []);
    } catch (error) {
      failureToast("Unable to load payment account status.");
    } finally {
      setLoading(false);
    }
  }, [axBe, storeCorrelationId]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const startOrContinue = async () => {
    if (!storeCorrelationId) return;
    setActionLoading(true);
    const onboardingWindow = window.open("about:blank", "_blank");
    if (onboardingWindow) {
      onboardingWindow.opener = null;
    }
    const endpoint = status?.action === "REFRESH" ? "refresh" : "start";
    try {
      const response = await axBe.post(
        `${API_ENDPOINT}finix/merchant-onboarding/${endpoint}`,
        { storeCorrelationId }
      );
      const onboardingUrl = response.data?.onboardingUrl;
      if (!onboardingUrl) {
        onboardingWindow?.close();
        setStatus(response.data);
        return;
      }
      if (onboardingWindow) {
        onboardingWindow.location.href = onboardingUrl;
      } else {
        failureToast("Please allow pop-ups to open Finix setup in a new tab.");
      }
    } catch (error) {
      onboardingWindow?.close();
      failureToast("Unable to start payment account setup.");
    } finally {
      setActionLoading(false);
    }
  };

  const createNewForm = async () => {
    if (!storeCorrelationId) return;
    setActionLoading(true);
    const onboardingWindow = window.open("about:blank", "_blank");
    if (onboardingWindow) {
      onboardingWindow.opener = null;
    }
    try {
      const response = await axBe.post(`${API_ENDPOINT}finix/merchant-onboarding/forms`, {
        storeCorrelationId,
      });
      const onboardingUrl = response.data?.onboardingUrl;
      if (onboardingUrl && onboardingWindow) {
        onboardingWindow.location.href = onboardingUrl;
      } else {
        onboardingWindow?.close();
        if (!onboardingUrl) {
          await loadStatus();
        } else {
          failureToast("Please allow pop-ups to open Finix setup in a new tab.");
        }
      }
      await loadStatus();
    } catch (error) {
      onboardingWindow?.close();
      failureToast("Unable to create a new onboarding form.");
    } finally {
      setActionLoading(false);
    }
  };

  const openForm = async (formId: string) => {
    if (!storeCorrelationId) return;
    setOpeningFormId(formId);
    const onboardingWindow = window.open("about:blank", "_blank");
    if (onboardingWindow) {
      onboardingWindow.opener = null;
    }
    try {
      const response = await axBe.post(
        `${API_ENDPOINT}finix/merchant-onboarding/forms/${encodeURIComponent(formId)}/link`,
        { storeCorrelationId }
      );
      const onboardingUrl = response.data?.onboardingUrl;
      if (onboardingUrl && onboardingWindow) {
        onboardingWindow.location.href = onboardingUrl;
      } else {
        onboardingWindow?.close();
        failureToast("Unable to open this onboarding form.");
      }
      await loadStatus();
    } catch (error) {
      onboardingWindow?.close();
      failureToast("Unable to open this onboarding form.");
    } finally {
      setOpeningFormId(null);
    }
  };

  const formatDate = (value?: string | number | null) => {
    if (!value) return "—";
    const numericValue = typeof value === "number" ? value : Number(value);
    const date = Number.isFinite(numericValue)
      ? new Date(numericValue < 100000000000 ? numericValue * 1000 : numericValue)
      : new Date(value);
    return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
  };

  const displayStatus =
    status?.merchantStatus || status?.onboardingStatus || status?.status || "NOT_STARTED";
  const canAct = status?.action === "START" || status?.action === "REFRESH";
  const canCreateNewForm = status?.merchantStatus !== "APPROVED" && status?.status !== "APPROVED";

  return (
    <div className="w-full min-w-0 max-w-[760px] space-y-3">
      <div className={cardClass}>
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <h2 className="text-[18px] font-semibold text-[#111827] tracking-[-0.02em]">
              Payment Account
            </h2>
            <p className="mt-2 text-[14px] text-[#6b7280]">
              Set up the restaurant payment account used to accept online card
              payments and receive payouts.
            </p>
          </div>
          <span
            className={`inline-flex w-fit rounded-full px-3 py-1 text-xs font-semibold ${
              status?.canAcceptPayments
                ? "bg-[#ecfdf3] text-[#027a48]"
                : "bg-[#fff7f4] text-[#FD7657]"
            }`}
          >
            {loading ? "Loading..." : statusLabels[displayStatus] || displayStatus}
          </span>
        </div>

        <div className="mt-6 rounded-xl border border-[#e5e7eb] bg-[#f9fafb] px-4 py-3">
          <p className="text-[14px] font-medium text-[#111827]">
            {status?.message || "Set up your payment account to accept card payments."}
          </p>
          <p className="mt-1 text-[13px] text-[#6b7280]">
            Provider: {status?.provider || "FINIX"}
          </p>
        </div>

        <div className="mt-5 flex flex-wrap gap-3">
          {canAct && (
            <button
              type="button"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                startOrContinue();
              }}
              disabled={actionLoading || loading}
              className="h-10 rounded-lg bg-[#FD7657] px-4 text-sm font-medium text-white disabled:opacity-60"
            >
              {actionLoading
                ? "Opening..."
                : status?.action === "REFRESH"
                ? "Continue setup"
                : "Set up payment account"}
            </button>
          )}
          {canCreateNewForm && (
            <button
              type="button"
              onClick={createNewForm}
              disabled={actionLoading || loading}
              className="h-10 rounded-lg bg-[#111827] px-4 text-sm font-medium text-white disabled:opacity-60"
            >
              {actionLoading ? "Opening..." : "Create new form"}
            </button>
          )}
          <button
            type="button"
            onClick={loadStatus}
            disabled={loading}
            className="h-10 rounded-lg border border-[#e5e7eb] bg-white px-4 text-sm font-medium text-[#111827] disabled:opacity-60"
          >
            Refresh status
          </button>
        </div>
      </div>

      <div className={cardClass}>
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="text-[16px] font-semibold text-[#111827]">
              Onboarding Forms
            </h3>
            <p className="mt-1 text-[13px] text-[#6b7280]">
              Previous Finix forms for this restaurant, newest first.
            </p>
          </div>
        </div>

        <div className="mt-4 divide-y divide-[#e5e7eb] rounded-xl border border-[#e5e7eb]">
          {forms.length === 0 ? (
            <div className="px-4 py-5 text-sm text-[#6b7280]">
              No onboarding forms have been created yet.
            </div>
          ) : (
            forms.map((form) => {
              const formStatus = form.status || "—";
              const opening = openingFormId === form.finixOnboardingFormId;
              const canOpen = form.action !== "WAIT" && formStatus !== "COMPLETED";
              return (
                <div
                  key={form.finixOnboardingFormId}
                  className="flex flex-col gap-4 px-4 py-4 md:flex-row md:items-center md:justify-between"
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="break-all text-sm font-semibold text-[#111827]">
                        {form.finixOnboardingFormId}
                      </p>
                      {form.current && (
                        <span className="rounded-full bg-[#eff6ff] px-2 py-0.5 text-xs font-semibold text-[#1d4ed8]">
                          Current
                        </span>
                      )}
                      <span className="rounded-full bg-[#f8fafc] px-2 py-0.5 text-xs font-semibold text-[#111827]">
                        {formStatus}
                      </span>
                    </div>
                    <div className="mt-2 grid gap-1 text-xs text-[#6b7280] sm:grid-cols-2">
                      <span>Created: {formatDate(form.createdAt)}</span>
                      <span>Last synced: {formatDate(form.lastSyncedAt)}</span>
                      <span className="break-all">Identity: {form.finixIdentityId || "—"}</span>
                      <span>Link expires: {formatDate(form.onboardingUrlExpiresAt)}</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => openForm(form.finixOnboardingFormId)}
                    disabled={!canOpen || opening || loading}
                    className="h-10 shrink-0 rounded-lg border border-[#e5e7eb] bg-white px-4 text-sm font-medium text-[#111827] disabled:opacity-60"
                  >
                    {opening ? "Opening..." : canOpen ? "Open form" : formStatus}
                  </button>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentAccountSetting;
